import type { FieldNode, Page } from '@fieldia/core';
import type { ElementFactory } from './chrome';
import { columnsEditor } from './columns-editor';
import type { Designer } from './designer';
import { WIDGET_ONLY_KINDS, kindOfField } from './kinds';
import { kindSettings } from './kind-settings';
import { inputSettings } from './kind-settings-inputs';
import { structureSettings } from './kind-settings-structures';
import { pickerSettings } from './kind-settings-picker';
import type { DesignerWords } from './designer-words';
import { settingNumber } from './number-box';

/**
 * What a kind of field has beyond its words, set in the picked field itself,
 * where it is seen: a rating's levels, where a scale starts and ends, an
 * amount's currency, what a link points to, a table's columns — and the
 * newer kinds' own, from `kind-settings`, and the structures', from
 * `kind-settings-structures`. A field of the model keeps the
 * model's, so it has none here. The panel has the same.
 */

export interface InlineSettings {
  element: HTMLElement;
  /** Draw for the field as it is now; true when the settings stand in for its widget, as a table's columns do. */
  update(page: Page, node: FieldNode): boolean;
}

const range = (from: number, to: number) => Array.from({ length: to - from + 1 }, (_, i) => String(from + i));

/** Kinds of file, as people name them, and the media types each takes. */
const FILE_TYPES: [keyof DesignerWords['questions']['fileTypes'], string[]][] = [
  ['images', ['image/*']],
  ['pdf', ['application/pdf']],
  ['documents', ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.oasis.opendocument.text', 'text/plain']],
  ['spreadsheets', ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.oasis.opendocument.spreadsheet', 'text/csv']],
  ['presentations', ['application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/vnd.oasis.opendocument.presentation']],
  ['video', ['video/*']],
  ['audio', ['audio/*']],
];
/** The largest file, in bytes, and in words. */
const FILE_SIZES = [1024 * 1024, 10 * 1024 * 1024, 100 * 1024 * 1024, 1024 * 1024 * 1024];
/** The longest sides photos are made smaller to, in pixels, and how sharp: a phone's screen, a laptop's, a print. */
const PHOTO_SIDES = [1280, 1600, 2048, 2560, 3200, 4096];
const PHOTO_QUALITIES = [0.7, 0.8, 0.9, 0.95];
/** How long a recording may be, offered as the longest, in seconds. */
const RECORD_LENGTHS = [30, 60, 120, 300, 600];

export function inlineSettings(el: ElementFactory, designer: Designer, id: string): InlineSettings {
  const w = designer.words.questions;
  const element = el('div', { class: 'fd-inline-settings', hidden: '' });
  const focused = (node: Element) => node.ownerDocument.activeElement === node;
  const select = (label: string, values: string[]) => el('select', { class: 'fd-inline-select', 'aria-label': label }, ...values.map((v) => el('option', { value: v }, v))) as HTMLSelectElement;
  const word = (text: string, control: HTMLElement) => el('label', { class: 'fd-inline-setting' }, el('span', {}, text), control);
  let drawn = '';
  let refresh: (page: Page, node: FieldNode) => void = () => undefined;
  let standsIn = false;

  /** How a link shows its record: a picture, lines under it, colours on tags, and whether it opens — Flectra's avatar widgets, show_address, color_field and no_open. */
  function linkLooks(several: boolean) {
    const switches = (several ? (['avatar', 'colors', 'open'] as const) : (['avatar', 'details', 'open'] as const)).map((key) => {
      const label = { avatar: w.linkPicture, details: w.linkLines, colors: w.tagColours, open: w.opensRecord }[key];
      const button = el('button', { type: 'button', class: 'fd-switch', role: 'switch', 'aria-checked': 'false', 'aria-label': label }) as HTMLButtonElement;
      // Opening is on unless turned off; the rest off unless turned on.
      button.addEventListener('click', () => {
        const on = button.getAttribute('aria-checked') !== 'true';
        designer.setWidgetOptions(id, { [key]: key === 'open' ? (on ? null : false) : on || null });
      });
      return { key, button, element: el('span', { class: 'fd-inline-setting fd-inline-toggle' }, button, el('span', { 'aria-hidden': 'true' }, label)) };
    });
    return {
      element: el('div', { class: 'fd-inline-row fd-link-looks' }, ...switches.map((s) => s.element)),
      refresh(node: FieldNode) {
        for (const { key, button } of switches) button.setAttribute('aria-checked', String(key === 'open' ? node.options?.['open'] !== false : node.options?.[key] === true));
      },
    };
  }

  function build(kind: string | null) {
    standsIn = false;
    refresh = () => undefined;
    if (kind === 'rating') {
      const levels = select(w.levels, range(3, 10));
      levels.addEventListener('change', () => designer.setRange(id, { min: 1, max: Number(levels.value) }));
      element.replaceChildren(word(w.levels, levels));
      refresh = (page, node) => {
        const def = page.fields[node.field];
        levels.value = String('max' in def && def.max !== undefined ? def.max : 5);
      };
    } else if (kind === 'scale') {
      const from = select(w.from, ['0', '1']);
      const to = select(w.toLabel, range(2, 10));
      const save = () => designer.setRange(id, { min: Number(from.value), max: Number(to.value) });
      from.addEventListener('change', save);
      to.addEventListener('change', save);
      // Words at either end, each typed beside its number, as Google Forms has them.
      const end = (which: 'start' | 'end') => {
        const number = el('span', { class: 'fd-inline-end-number' });
        const words = el('input', { class: 'fd-inline-input fd-inline-end-words', 'aria-label': w.wordsAt(which), placeholder: w.labelOptional, autocomplete: 'off' }) as HTMLInputElement;
        words.addEventListener('input', () => designer.setWidgetOptions(id, { [`${which}Label`]: words.value }));
        return { element: el('label', { class: 'fd-inline-end' }, number, words), number, words };
      };
      const start = end('start');
      const finish = end('end');
      element.replaceChildren(el('div', { class: 'fd-inline-row' }, word(w.from, from), word(w.to, to)), start.element, finish.element);
      refresh = (page, node) => {
        const def = page.fields[node.field];
        const min = 'min' in def && def.min !== undefined ? def.min : 0;
        const max = 'max' in def && def.max !== undefined ? def.max : 10;
        from.value = String(min);
        to.value = String(max);
        start.number.textContent = String(min);
        finish.number.textContent = String(max);
        if (!focused(start.words)) start.words.value = String(node.options?.['startLabel'] ?? '');
        if (!focused(finish.words)) finish.words.value = String(node.options?.['endLabel'] ?? '');
      };
    } else if (kind === 'amount') {
      const currency = el('input', { class: 'fd-inline-input fd-inline-currency', 'aria-label': w.currency, maxlength: '3', autocomplete: 'off', spellcheck: 'false' }) as HTMLInputElement;
      // Three letters make a currency; until then nothing is saved.
      currency.addEventListener('input', () => /^[A-Za-z]{3}$/.test(currency.value.trim()) && designer.setCurrency(id, currency.value));
      element.replaceChildren(word(w.currency, currency));
      refresh = (page, node) => {
        const def = page.fields[node.field];
        if (!focused(currency)) currency.value = def.type === 'monetary' ? (def.currency ?? '') : '';
      };
    } else if (kind === 'link' || kind === 'links' || kind === 'model:link' || kind === 'model:links') {
      // What it points to — the page's own field only — and how it shows what it points to, whoever's field it is.
      const own = !kind.startsWith('model:');
      const target = el('input', { class: 'fd-inline-input', 'aria-label': w.linksTo, placeholder: 'contact', autocomplete: 'off', spellcheck: 'false' }) as HTMLInputElement;
      target.addEventListener('input', () => target.value.trim() && designer.setRelation(id, target.value));
      const several = kind.endsWith('links');
      const looks = linkLooks(several);
      element.replaceChildren(...(own ? [word(w.linksTo, target)] : []), looks.element);
      refresh = (page, node) => {
        const def = page.fields[node.field];
        if (own && !focused(target)) target.value = 'relation' in def ? def.relation : '';
        looks.refresh(node);
      };
    } else if (kind === 'file' || kind === 'image' || kind === 'photos') {
      // Which kinds of file it takes — none picked, any; an image takes images — and the largest.
      const types = FILE_TYPES.map(([label, accept]) => {
        const button = el('button', { type: 'button', class: 'fd-inline-chip', 'aria-pressed': 'false' }, w.fileTypes[label]) as HTMLButtonElement;
        button.addEventListener('click', () => {
          const on = new Set(current());
          const all = accept.every((a) => on.has(a));
          for (const a of accept) {
            if (all) on.delete(a);
            else on.add(a);
          }
          designer.setFileRules(id, { accept: FILE_TYPES.flatMap(([, list]) => list).filter((a) => on.has(a)) });
        });
        return { button, accept };
      });
      let current: () => string[] = () => [];
      const largest = select(w.largestFile, ['', ...FILE_SIZES.map(String)]);
      [w.anySize, ...FILE_SIZES.map(w.size)].forEach((words, i) => (largest.options[i].textContent = words));
      // A size the page set that is none of these is shown as it is.
      const own = el('option', { hidden: '' }) as HTMLOptionElement;
      largest.append(own);
      largest.addEventListener('change', () => designer.setFileRules(id, { maxSize: largest.value ? Number(largest.value) : null }));
      // Several files, as few and as many as it takes: a box left empty is no limit. Saved once typed and left.
      const several = el('button', { type: 'button', class: 'fd-switch', role: 'switch', 'aria-checked': 'false', 'aria-label': w.severalFiles }) as HTMLButtonElement;
      several.addEventListener('click', () => designer.setFileRules(id, { multiple: several.getAttribute('aria-checked') !== 'true' }));
      const held: Record<'minFiles' | 'maxFiles', number | null> = { minFiles: null, maxFiles: null };
      const count = (label: string, key: 'minFiles' | 'maxFiles') =>
        settingNumber(el, designer, {
          label,
          attrs: { class: 'fd-inline-input fd-inline-number', placeholder: w.any },
          integer: true,
          min: key === 'minFiles' ? 0 : 1,
          optional: true,
          saved: () => held[key],
          commit: (n) => designer.setFileRules(id, { [key]: n }),
        });
      const least = count(w.atLeast, 'minFiles');
      const most = count(w.atMost, 'maxFiles');
      // Each box with its word for what it counts, so the two never part on a narrow card.
      const files = (text: string, box: HTMLElement) => el('label', { class: 'fd-inline-setting' }, el('span', {}, text), box, el('span', {}, w.files));
      const counts = el('div', { class: 'fd-inline-row' }, files(w.atLeast, least.input), files(w.atMost, most.input));
      // How the chosen files show: as a list, as thumbnails or as cards; what the kind shows anyway is kept as nothing.
      // And whether the person filling the form can switch them between a list and the pictures.
      let shownAs = 'list';
      let usual = 'list';
      const asChip = (value: string, words: string) => {
        const chip = el('button', { type: 'button', class: 'fd-inline-chip', 'aria-pressed': 'false', 'data-choice': value }, words) as HTMLButtonElement;
        chip.addEventListener('click', () => value !== shownAs && designer.setWidgetOptions(id, { files: value === usual ? null : value }));
        return chip;
      };
      const asChips = [asChip('list', w.list), asChip('thumbnails', w.thumbnails), asChip('cards', w.cards)];
      const switchable = el('button', { type: 'button', class: 'fd-switch', role: 'switch', 'aria-checked': 'false', 'aria-label': w.peopleCanSwitch }) as HTMLButtonElement;
      switchable.addEventListener('click', () => designer.setWidgetOptions(id, { filesSwitch: switchable.getAttribute('aria-checked') === 'true' ? null : true }));
      // Photos made smaller before they are kept: as usual (an image's, where files are stored), kept as taken, or to a side;
      // and how sharp, once they are made smaller. A side the page set that is none of these is shown as it is.
      const smaller = select(w.smallerPhotos, ['', 'off', ...PHOTO_SIDES.map(String)]);
      const ownSide = el('option', { hidden: '' }) as HTMLOptionElement;
      smaller.append(ownSide);
      const quality = select(w.photoQuality, ['', ...PHOTO_QUALITIES.map(String)]);
      [w.qualityUsual, ...PHOTO_QUALITIES.map((q) => w.qualityOf(Math.round(q * 100)))].forEach((words, i) => (quality.options[i].textContent = words));
      const shrinkFrom = () => {
        if (smaller.value === 'off') return false;
        const side = smaller.value ? Number(smaller.value) : undefined;
        const sharp = quality.value ? Number(quality.value) : undefined;
        return side === undefined && sharp === undefined ? null : { ...(side !== undefined ? { maxSide: side } : {}), ...(sharp !== undefined ? { quality: sharp } : {}) };
      };
      smaller.addEventListener('change', () => designer.setFileRules(id, { shrink: shrinkFrom() }));
      quality.addEventListener('change', () => designer.setFileRules(id, { shrink: shrinkFrom() }));
      const qualityRow = word(w.photoQuality, quality);
      // A phone's camera, for an image: the rear one, or the front.
      const camera = select(w.cameraOnPhones, ['', 'environment', 'user']);
      [w.no, w.rearCamera, w.frontCamera].forEach((words, i) => (camera.options[i].textContent = words));
      camera.addEventListener('change', () => designer.setWidgetOptions(id, { camera: camera.value === 'user' ? 'user' : camera.value ? true : null }));
      // A photo set is always several photos, drawn as its tiles: neither is a choice there.
      const set = kind === 'photos';
      // A voice note or a video recorded on the page, for a file upload, and the longest a recording may be.
      const recorded = select(w.recordedOnPage, Object.keys(w.recordKinds));
      Object.values(w.recordKinds).forEach((words, i) => (recorded.options[i].textContent = words));
      recorded.addEventListener('change', () => designer.setWidgetOptions(id, { record: recorded.value === 'both' ? ['audio', 'video'] : recorded.value || null, ...(recorded.value ? {} : { maxSeconds: null }) }));
      const longest = select(w.longestRecording, ['', ...RECORD_LENGTHS.map(String)]);
      [w.recordUsual, ...RECORD_LENGTHS.map(w.recordFor)].forEach((words, i) => (longest.options[i].textContent = words));
      const ownLength = el('option', { hidden: '' }) as HTMLOptionElement;
      longest.append(ownLength);
      longest.addEventListener('change', () => designer.setWidgetOptions(id, { maxSeconds: longest.value ? Number(longest.value) : null }));
      const longestRow = word(w.longestRecording, longest);
      element.replaceChildren(
        ...(kind === 'file' ? [el('div', { class: 'fd-inline-row' }, el('span', {}, w.takesOnly), el('div', { class: 'fd-inline-chips', role: 'group', 'aria-label': w.kindsOfFile }, ...types.map((t) => t.button)))] : []),
        el('div', { class: 'fd-inline-row' }, word(w.largestFile, largest), ...(set ? [] : [el('span', { class: 'fd-inline-setting fd-inline-toggle' }, several, el('span', { 'aria-hidden': 'true' }, w.severalFiles))])),
        counts,
        ...(set
          ? []
          : [
              el('div', { class: 'fd-inline-row' }, el('span', {}, w.showFilesAs), el('div', { class: 'fd-inline-chips', role: 'group', 'aria-label': w.showFilesAs }, ...asChips)),
              el('span', { class: 'fd-inline-setting fd-inline-toggle' }, switchable, el('span', { 'aria-hidden': 'true' }, w.peopleCanSwitch)),
            ]),
        word(w.smallerPhotos, smaller),
        qualityRow,
        ...(kind !== 'file' ? [word(w.cameraOnPhones, camera)] : []),
        ...(kind === 'file' ? [word(w.recordedOnPage, recorded), longestRow] : [])
      );
      refresh = (page, node) => {
        const def = page.fields[node.field];
        if (def.type !== 'binary' && def.type !== 'image') return;
        const accept = def.type === 'binary' ? (def.accept ?? []) : [];
        current = () => accept;
        for (const t of types) t.button.setAttribute('aria-pressed', String(t.accept.every((a) => accept.includes(a))));
        own.hidden = !def.maxSize || FILE_SIZES.includes(def.maxSize);
        own.value = String(def.maxSize ?? '');
        own.textContent = def.maxSize ? w.size(def.maxSize) : '';
        largest.value = String(def.maxSize ?? '');
        several.setAttribute('aria-checked', String(def.multiple === true));
        counts.hidden = def.multiple !== true;
        held.minFiles = def.minFiles ?? null;
        held.maxFiles = def.maxFiles ?? null;
        least.refresh();
        most.refresh();
        usual = def.type === 'image' ? 'thumbnails' : 'list';
        shownAs = String(node.options?.['files'] ?? usual);
        for (const chip of asChips) chip.setAttribute('aria-pressed', String(chip.dataset['choice'] === shownAs));
        switchable.setAttribute('aria-checked', String(node.options?.['filesSwitch'] === true));
        const facing = node.options?.['camera'];
        camera.value = facing ? (facing === 'user' ? 'user' : 'environment') : '';
        const record = node.options?.['record'];
        const kinds = (Array.isArray(record) ? record : [record]).filter((one) => one === 'audio' || one === 'video');
        recorded.value = kinds.length === 2 ? 'both' : String(kinds[0] ?? '');
        const seconds = node.options?.['maxSeconds'];
        ownLength.hidden = typeof seconds !== 'number' || RECORD_LENGTHS.includes(seconds);
        ownLength.value = String(seconds ?? '');
        ownLength.textContent = typeof seconds === 'number' ? w.recordFor(seconds) : '';
        longest.value = typeof seconds === 'number' ? String(seconds) : '';
        // How long, only where something is recorded: hidden, not disabled, where nothing is.
        longestRow.hidden = !kinds.length;
        // An image is made smaller as usual, and may be kept as taken; a file upload is not, unless a side is picked.
        const picture = def.type === 'image';
        smaller.options[0].textContent = picture ? w.smallerUsual : w.smallerNo;
        smaller.options[1].textContent = w.smallerNo;
        smaller.options[1].hidden = !picture;
        PHOTO_SIDES.forEach((side, i) => (smaller.options[i + 2].textContent = w.smallerTo(side)));
        const shrink = def.shrink;
        const side = shrink ? shrink.maxSide : undefined;
        ownSide.hidden = side === undefined || PHOTO_SIDES.includes(side);
        ownSide.value = String(side ?? '');
        ownSide.textContent = side !== undefined ? w.smallerTo(side) : '';
        smaller.value = shrink === false ? (picture ? 'off' : '') : String(side ?? '');
        quality.value = shrink && shrink.quality !== undefined && PHOTO_QUALITIES.includes(shrink.quality) ? String(shrink.quality) : '';
        // How sharp, only where photos are made smaller: hidden, not disabled, where they are kept as taken.
        qualityRow.hidden = shrink === false || (!picture && !shrink);
      };
    } else if (kind === 'lines') {
      const columns = columnsEditor(el, designer, id);
      element.replaceChildren(columns.element);
      standsIn = true;
      refresh = (page, node) => columns.update(page.fields[node.field]);
    } else {
      // The newer kinds, and the choices' Shuffle and points.
      const more = kindSettings(el, designer, id, kind);
      element.replaceChildren(...(more?.elements ?? []));
      standsIn = more?.standsIn ?? false;
      if (more) refresh = more.refresh;
    }
    // The text, number and date kinds' own, then the structures', after the rest of their kind's.
    const inputs = inputSettings(el, designer, id, kind);
    // The work tracker's pickers' own (kind-settings-picker): a path, tags from a button, an own list, a state's colours.
    const structures = [...structureSettings(el, designer, id, kind), ...pickerSettings(el, designer, id, kind)];
    if (!inputs && !structures.length) return;
    element.append(...(inputs?.elements ?? []), ...structures.map((p) => p.element));
    // A quiz's question's own settings close the row, after its kind's (a short answer's Most characters).
    element.append(...element.querySelectorAll(':scope > .fd-kind-quiz'));
    const before = refresh;
    refresh = (page, node) => (before(page, node), inputs?.refresh(page, node), structures.forEach((p) => p.refresh(page, node)));
  }

  return {
    element,
    update(page, node) {
      const def = page.fields[node.field];
      // A field of the model keeps what it holds: how a link shows its record is the page's still,
      // and a widget's own settings are offered; the rest are the model's.
      const own = kindOfField(def, node);
      const kind = !designer.isFromModel(id) ? own : own === 'link' || own === 'links' ? `model:${own}` : WIDGET_ONLY_KINDS.has(own ?? '') ? own : null;
      if ((kind ?? '') !== drawn) {
        drawn = kind ?? '';
        build(kind);
      }
      refresh(page, node);
      // Hidden with nothing in it to show: a quiz's settings on a question of a form that is no quiz yet.
      element.hidden = !element.childElementCount || [...element.children].every((child) => (child as HTMLElement).hidden);
      return standsIn && !element.hidden;
    },
  };
}
